import React, { useEffect, useState } from "react";
import Table from "./Table";

import { useLazyQuery, gql, useMutation } from "@apollo/client";
import { Spin } from "antd";

import { CSVLink } from "react-csv";
import FileUpload from "../Events/components/FileUpload/FileUpload";
import moment from "moment";
import { StringLiteral } from "typescript";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import HandleFacility from "./Facility/HandleFacility";

/* =======================
   GraphQL
======================= */

const DATA_FACILITIES = gql`
  query ($sortInput: FacilitySort) {
    facilityOverview(sort: $sortInput) {
      results {
        id
        facilityName
        facilityCode
        facilityContact
        facilityDirectAddress
      }
    }
  }
`;

const DELETE_FACILITY = gql`
  mutation deleteFacility($id: ID!) {
    deleteFacility(id: $id) {
      success
      message
    }
  }
`;

/* =======================
   Component
======================= */

export default function Facility() {
  const [displayData, setDisplayData] = useState<any>([]);
  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [currentScreen, setCurrentScreen] = useState<"table" | "manage">(
    "table"
  );
  const [editFacilityData, setEditFacilityData] = useState<any>(null);

  const [deleteFacility] = useMutation(DELETE_FACILITY);

  const [getFacilities, { loading, data }] = useLazyQuery(
    DATA_FACILITIES,
    {
      fetchPolicy: "network-only",
      onCompleted: (completeData) => {
        const tempArr: any[] = [];

        tempArr.push(["Name", "Code", "Contact"]);

        completeData.facilityOverview.results.forEach((value: any) => {
          tempArr.push([
            value.facilityName,
            value.facilityCode || "",
            value.facilityContact || "",
          ]);
        });

        setDisplayData(tempArr);
      },
    }
  );

  /* =======================
     Lifecycle
  ======================= */

  useEffect(() => {
    getDefaultData();
  }, []);

  const getDefaultData = () => {
    getFacilities({
      variables: {
        sortInput: { id: -1 },
      },
    });
  };

  /* =======================
     Sorting
  ======================= */

  const handleSorting = (index: any) => {
    const sortHeader = ["facilityName", "facilityCode"][index];

    const sortOrder =
      index === sortField && order === "asc" ? "desc" : "asc";

    const sortInput = {
      [sortHeader]: sortOrder === "asc" ? 1 : -1,
    };

    setSortField(index);
    setOrder(sortOrder);

    getFacilities({
      variables: { sortInput },
    });
  };

  /* =======================
     Delete
  ======================= */

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this facility?"))
      return;

    try {
      const response = await deleteFacility({
        variables: { id },
      });

      if (response.data?.deleteFacility?.success) {
        getDefaultData();
      } else {
        alert(response.data?.deleteFacility?.message || "Delete failed");
      }
    } catch (error: any) {
      alert(error.message || "Something went wrong");
    }
  };

  /* =======================
     UI
  ======================= */

  return (
    <>
      {loading || data === undefined ? (
        <Spin />
      ) : (
        <>
          {currentScreen === "table" ? (
            <>
              {/* Header */}
              <div className="exportuploadbuttonposition">
                <CSVLink
                  data={displayData}
                  filename={`Facilities_${moment().format(
                    "MM/DD/YYYY_HH:mm:ss"
                  )}.csv`}
                  className="export-button"
                >
                  Export
                </CSVLink>

                <button
                  className="primary-button"
                  onClick={() => {
                    setEditFacilityData(null);
                    setCurrentScreen("manage");
                  }}
                >
                  Add Facility
                </button>
              </div>

              {/* Table */}
              <Table
                headers={["Name", "Code", "Contact", "Actions"]}
                headersSortBool={[true, true, false, false]}
                data={data.facilityOverview.results.map((value: any) => [
                  value.facilityName,
                  value.facilityCode || "",
                  value.facilityContact || "",
                  <div style={{ display: "flex", gap: 12 }}>
                    <EditOutlined
                      style={{ color: "#1677ff", cursor: "pointer" }}
                      onClick={() => {
                        setEditFacilityData({
                          id: value.id,
                          facilityName: value.facilityName,
                          facilityCode: value.facilityCode,
                          facilityContact: value.facilityContact,
                          facilityDirectAddress:
                            value.facilityDirectAddress,
                        });
                        setCurrentScreen("manage");
                      }}
                    />

                    <DeleteOutlined
                      style={{ color: "red", cursor: "pointer" }}
                      onClick={() => handleDelete(value.id)}
                    />
                  </div>,
                ])}
                handleSorting={handleSorting}
                order={order}
                field={sortField}
              />
            </>
          ) : (
            <HandleFacility
              editFacilityData={editFacilityData}
              onClose={() => {
                setEditFacilityData(null);
                setCurrentScreen("table");
                getDefaultData();
              }}
              refreshData={getDefaultData}
            />
          )}
        </>
      )}
    </>
  );
}